/**
 * Browser-safe filename / storage-key helpers for uploaded file fonts.
 *
 * The frontend editor and the backend producer must agree on the three
 * historical `font-family` names for one file (typographic family,
 * filename stem, storage-key basename). Only `font-validation` is
 * imported so the app can load this without the server logger or Node
 * built-ins.
 */

import { sanitizeFamilyName } from "./font-validation";

/**
 * Upload keys rewrite characters outside this set to `_`.
 */
const UPLOAD_UNSAFE_FILENAME_CHARS = /[^a-zA-Z0-9._-]/g;

/**
 * The `font-family` name renderers use for an uploaded file font: the filename
 * with its extension stripped (matches `useBrandFonts` + Lambda).
 */
export function normalizeFileName(fileName: string | undefined): string | null {
  if (!fileName) return null;
  return fileName.replace(/\.[^.]+$/, "");
}

function uploadSanitizedStem(fileStem: string): string {
  return fileStem.replace(UPLOAD_UNSAFE_FILENAME_CHARS, "_");
}

function isPrefixedStorageName(storage: string, fileStem: string): boolean {
  if (!fileStem || storage === fileStem) {
    return false;
  }
  return storage.endsWith(`-${fileStem}`);
}

/**
 * CSS family name derived from the S3 storage-key basename when that
 * basename is the original file stem with an upload prefix
 * (`…/1779217721131-Inter-ExtraBold.otf` + `Inter-ExtraBold` →
 * `1779217721131-Inter-ExtraBold`). Returns null when the key is a
 * short test id or otherwise unrelated to `fileStem`, so renderers
 * don't register junk aliases.
 *
 * Upload sanitizes the filename before prefixing, so a stem with spaces
 * (`Inter ExtraBold`) is stored as `1779-Inter_ExtraBold`. Both the raw
 * and sanitized stems are accepted.
 */
export function storageFontName(
  fileId: string | undefined,
  fileStem?: string | null,
): string | null {
  if (!fileId) return null;
  const base = fileId.split("/").pop();
  if (!base) return null;
  const storage = normalizeFileName(base);
  if (!storage) return null;
  if (fileStem) {
    const sanitizedStem = uploadSanitizedStem(fileStem);
    if (
      !isPrefixedStorageName(storage, fileStem) &&
      !isPrefixedStorageName(storage, sanitizedStem)
    ) {
      return null;
    }
  }
  // `fileId` is Hasura-writable. Reject anything that would break out of
  // a CSS `font-family: "..."` interpolation.
  return sanitizeFamilyName(storage);
}

/**
 * Filename / storage-key aliases that are not the typographic family.
 * Renderers register each of these alongside `family` so markup can
 * reference any of the three historical names.
 */
export function fileFontAliasNames(args: {
  name: string;
  family?: string;
  storageName?: string;
}): string[] {
  const aliases: string[] = [];
  if (args.name && args.name !== args.family) {
    aliases.push(args.name);
  }
  if (
    args.storageName &&
    args.storageName !== args.name &&
    args.storageName !== args.family
  ) {
    aliases.push(args.storageName);
  }
  return aliases;
}
